// src/pages/CRM/Finance/InvoiceDetail.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, Divider, Grid, CircularProgress, Alert,
  Stack, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import axiosInstance from '../../../../utils/axiosInstance';
import { InvoiceStatus, InvoiceStatusLabels, InvoiceStatusColors, PaymentMethodLabels } from '../Core/crmEnums';
import PaymentDialog from './PaymentDialog';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const InvoiceDetail = () => {
  const { chatbotId, invoiceId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  useEffect(() => {
    document.title = 'Invoice Detail | KlonIT CRM';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadInvoice();
    }
  }, [canView, invoiceId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadInvoice = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/api/crm/invoices/${invoiceId}`);
      if (response.data.success) {
        setInvoice(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load invoice');
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post(`/api/crm/invoices/${invoiceId}/send`);
      if (response.data.success) {
        enqueueSnackbar('Invoice sent successfully', { variant: 'success' });
        loadInvoice();
      } else {
        enqueueSnackbar(response.data.message || 'Failed to send invoice', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error sending invoice:', err);
      enqueueSnackbar('Failed to send invoice', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentSaved = () => {
    setPaymentDialogOpen(false);
    loadInvoice();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view this invoice.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadInvoice}>Retry</Button>
        }>{error}</Alert>
      </Box>
    );
  }

  if (!invoice) return null;

  const lineItems = invoice.items || [];
  const payments = invoice.payments || [];
  const isDraft = invoice.status === InvoiceStatus.DRAFT;
  const canRecordPayment = invoice.status !== InvoiceStatus.PAID && invoice.status !== InvoiceStatus.CANCELLED;

  return (
    <Box sx={{ p: 3, fontFamily }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/salestransaction/invoices`)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
            Invoice {invoice.invoice_number}
          </Typography>
          <Chip
            label={InvoiceStatusLabels[invoice.status] || invoice.status}
            color={InvoiceStatusColors[invoice.status] || 'default'}
            sx={{ fontFamily, ml: 1 }}
          />
        </Stack>
        {canModify && (
          <Stack direction="row" spacing={1}>
            {isDraft && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/chatbot/${chatbotId}/crm/salestransaction/invoices/${invoiceId}/edit`)}
                  sx={{ fontFamily, textTransform: 'none' }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSendInvoice}
                  disabled={actionLoading}
                  sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                >
                  Send Invoice
                </Button>
              </>
            )}
            {canRecordPayment && (
              <Button
                variant="contained"
                startIcon={<PaymentIcon />}
                onClick={() => setPaymentDialogOpen(true)}
                sx={{ fontFamily, textTransform: 'none', bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
              >
                Record Payment
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Invoice Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily, mb: 2 }}>
              Invoice Details
            </Typography>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ fontFamily, color: '#6b7280' }}>Invoice Number</Typography>
                <Typography sx={{ fontFamily, fontWeight: 500 }}>{invoice.invoice_number}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontFamily, color: '#6b7280' }}>Invoice Date</Typography>
                <Typography sx={{ fontFamily }}>{formatDate(invoice.invoice_date)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontFamily, color: '#6b7280' }}>Due Date</Typography>
                <Typography sx={{ fontFamily }}>{formatDate(invoice.due_date)}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Contact Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily, mb: 2 }}>
              Contact
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                <Typography sx={{ fontFamily }}>{invoice.contact ? `${invoice.contact.first_name || ''} ${invoice.contact.last_name || ''}`.trim() : '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                <Typography sx={{ fontFamily, color: '#6b7280' }}>{invoice.contact?.email || '-'}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Totals */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily, mb: 2 }}>
              Summary
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily, color: '#6b7280' }}>Subtotal</Typography>
                <Typography sx={{ fontFamily }}>{formatCurrency(invoice.subtotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily, color: '#6b7280' }}>Tax ({invoice.tax_rate || 0}%)</Typography>
                <Typography sx={{ fontFamily }}>{formatCurrency(invoice.tax_amount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily, color: '#6b7280' }}>Discount</Typography>
                <Typography sx={{ fontFamily, color: '#ef4444' }}>-{formatCurrency(invoice.discount_amount)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily, fontWeight: 700 }}>Total</Typography>
                <Typography sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(invoice.total_amount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily, color: '#22c55e' }}>Paid</Typography>
                <Typography sx={{ fontFamily, color: '#22c55e' }}>{formatCurrency(invoice.amount_paid)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#ef4444' }}>Balance Due</Typography>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#ef4444' }}>
                  {formatCurrency(invoice.balance_due)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily }}>
                Line Items
              </Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Description</TableCell>
                    <TableCell align="center" sx={{ fontFamily, fontWeight: 600 }}>Quantity</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Unit Price</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, fontFamily, color: '#9ca3af' }}>
                        No line items
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily }}>{item.description}</TableCell>
                        <TableCell align="center" sx={{ fontFamily }}>{item.quantity}</TableCell>
                        <TableCell align="right" sx={{ fontFamily }}>{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily, fontWeight: 500 }}>{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Notes & Terms */}
        {(invoice.notes || invoice.terms) && (
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {invoice.notes && (
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <Typography variant="subtitle2" sx={{ fontFamily, fontWeight: 600, mb: 1 }}>Notes</Typography>
                    <Typography sx={{ fontFamily, color: '#6b7280', whiteSpace: 'pre-wrap' }}>{invoice.notes}</Typography>
                  </Paper>
                </Grid>
              )}
              {invoice.terms && (
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <Typography variant="subtitle2" sx={{ fontFamily, fontWeight: 600, mb: 1 }}>Terms & Conditions</Typography>
                    <Typography sx={{ fontFamily, color: '#6b7280', whiteSpace: 'pre-wrap' }}>{invoice.terms}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}

        {/* Payments */}
        <Grid item xs={12}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily }}>
                Payments ({payments.length})
              </Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Method</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Reference #</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, fontFamily, color: '#9ca3af' }}>
                        No payments recorded
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell sx={{ fontFamily }}>{formatDate(payment.payment_date)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#22c55e' }}>
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={PaymentMethodLabels[payment.payment_method] || payment.payment_method}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily, color: '#6b7280' }}>{payment.reference_number || '-'}</TableCell>
                        <TableCell sx={{ fontFamily, color: '#6b7280' }}>{payment.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onSaved={handlePaymentSaved}
        contactId={invoice.contact_id}
        invoiceId={invoice.id}
      />
    </Box>
  );
};

export default InvoiceDetail;
